// @flow strict

import React from 'react';
import { injectIntl } from 'react-intl';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { createStructuredSelector } from 'reselect';

// Constants
import { LegacyTheme, RadioButtonSet, PrimaryButton } from 'components/_ReactUI_V1';

import {
    SENSITIVITY_OPTIONS,
    THREE_D_SENSITIVITY_OUTPUT_OPTIONS,
    SENSITIVITY_OPTION_LIMITS,
    STYLE_VALUES,
} from 'utils/constants';

// Styles
import { SidebarBody, BodySection, FooterSection } from '../styles';
import {
    VariableBodySection,
    VariableTitle,
} from './styles';

// Components
import SensitivityVariableOption from '../SensitivityVariable';

import { fetchReagentInfoOfDataset } from 'services/Dataset/thunks';
import { selectDatasetReagentInfo, isFetchingDatasetReagentInfo } from 'services/Dataset/selectors';

// Types
import type { IntlType } from 'types';
import type {
    SensitivityOption,
    SensitivityOutputOption,
} from 'containers/CircuitComputationContainer/ThreeDSensitivityContainer';
import type { ReagentInfoForDataset } from 'services/Dataset/types';

type VariableType = 'X' | 'Y';

type Props = {
    intl: IntlType,

    sensitivityOptionAvailability: SensitivityOptionsAvailability,

    handleGenerateDiagramClicked: (
        xOptions: {
            sensitivityOption: SensitivityOption,
            start: ?number,
            finish: ?number,
        },
        yOptions: {
            sensitivityOption: SensitivityOption,
            start: ?number,
            finish: ?number,
        },
        zOption: SensitivityOutputOption
    ) => void,

    loading: boolean,


    /**
     * The reagent limits for the dataset's reagent
     * Only fetched when the x/y variable is set to reagent concentration
     */
    datasetId: number,
    reagentInfo: ?ReagentInfoForDataset,
    isFetchingReagentInfo: boolean,
    fetchReagentInfoOfDataset: (id: number) => void,
};

type State = {
    selectedVariableX: SensitivityOption,
    startX: ?number,
    finishX: ?number,

    selectedVariableY: SensitivityOption,
    startY: ?number,
    finishY: ?number,

    selectedOutputVariable: SensitivityOutputOption,
};

/**
 * The sidebar body section for the 3D sensitivity plot.
 */
class ThreeDSensitivitySidebarSection extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            selectedVariableX: SENSITIVITY_OPTIONS.PLS_COMPOSITION,
            startX: SENSITIVITY_OPTION_LIMITS.PLS_COMPOSITION.DEFAULT_MINIMUM,
            finishX: SENSITIVITY_OPTION_LIMITS.PLS_COMPOSITION.DEFAULT_MAXIMUM,

            selectedVariableY: SENSITIVITY_OPTIONS.PLS_PH, // sensitivity option cannot be the same as X
            startY: SENSITIVITY_OPTION_LIMITS.PLS_PH.DEFAULT_MINIMUM,
            finishY: SENSITIVITY_OPTION_LIMITS.PLS_PH.DEFAULT_MAXIMUM,

            selectedOutputVariable: THREE_D_SENSITIVITY_OUTPUT_OPTIONS.OVERALL_RECOVERY,
        };
    }

    componentDidMount() {
        this.checkAndFetchReagentInfoForDataset();
    }

    componentDidUpdate(prevProps: Props) {
        if(prevProps.datasetId !== this.props.datasetId) {
            this.checkAndFetchReagentInfoForDataset();
        }
    }

    checkAndFetchReagentInfoForDataset = () => {
        const { datasetId, reagentInfo } = this.props;
        if (reagentInfo && reagentInfo.datasetId === datasetId) {
            // We already have the info for this dataset
            return;
        }

        if (this.props.isFetchingReagentInfo) {
            // Already fetching, no need to re-fetch.
            return;
        }

        this.props.fetchReagentInfoOfDataset(datasetId);
    }

    /**
     * Get the radio button options for the output variable.
     */
    getOutputRadioOptions = () =>
        Object.keys(THREE_D_SENSITIVITY_OUTPUT_OPTIONS).map((value: string) => ({
            value,
            label: this.props.intl.formatMessage({
                id: `components.SensitivityDiagrams.Axis.${value}`,
            }),
        }));

    /**
     * Is the range of the variable input okay?
     */
    isRangeOkay = (optionType: VariableType) => {
        const option = this.state[`selectedVariable${optionType}`];
        if (!option) {
            return false;
        }

        const start = this.state[`start${optionType}`];
        const finish = this.state[`finish${optionType}`];

        // Make sure that if the user has specified both, that the startX is less than the finishX.
        if (start !== null && start !== null) {
            if (start >= finish) {
                return false;
            }
        }

        // Lets assume that the SensitivityVariable component already ensures ranges
        return start !== null && finish !== null;
    };

    /**
     * Gets whether the generate button is enabled.
     */
    isGenerateButtonEnabled = () =>
        Boolean(this.state.selectedVariableX) &&
        Boolean(this.state.selectedVariableY) &&
        this.isRangeOkay('X') &&
        this.isRangeOkay('Y');

    /**
     * Handle radio button change for the output option.
     */
    handleOutputSelected = (selectedOutputVariable: SensitivityOutputOption) =>
        this.setState({ selectedOutputVariable });

    /**
     * Generate the diagram.
     */
    handleGenerateDiagramClicked = () =>
        this.isGenerateButtonEnabled() &&
        this.props.handleGenerateDiagramClicked(
            {
                sensitivityOption: this.state.selectedVariableX,
                start: this.state.startX,
                finish: this.state.finishX,
            },
            {
                sensitivityOption: this.state.selectedVariableY,
                start: this.state.startY,
                finish: this.state.finishY,
            },
            this.state.selectedOutputVariable
        );

    render() {
        return (
            <SidebarBody>
                <BodySection>
                    <VariableBodySection>
                        <VariableTitle>
                            <span>
                                {this.props.intl.formatMessage({
                                    id: `components.CircuitComputationSidebar.3DSensitivity.variableZ.title`,
                                })}
                            </span>
                        </VariableTitle>
                        <RadioButtonSet
                            value={this.state.selectedOutputVariable}
                            options={this.getOutputRadioOptions()}
                            onClick={this.handleOutputSelected}
                            disabled={this.props.loading}
                            styles={{
                                labelColor: LegacyTheme.defaultColor,
                                labelFontSize: '13px',
                            }}
                        />
                    </VariableBodySection>
                    <SensitivityVariableOption
                        variableKey="X"
                        mode="3D"
                        sensitivityOptionAvailability={this.props.sensitivityOptionAvailability}
                        {...this.state}
                        onVariableChange={this.setState.bind(this)}
                    />
                    <SensitivityVariableOption
                        variableKey="Y"
                        mode="3D"
                        sensitivityOptionAvailability={this.props.sensitivityOptionAvailability}
                        {...this.state}
                        onVariableChange={this.setState.bind(this)}
                    />
                </BodySection>
                <FooterSection withMargin>
                    <PrimaryButton
                        text={this.props.intl.formatMessage({
                            id: `components.CircuitComputationSidebar.3DSensitivity.generatePlotButton`,
                        })}
                        loading={this.props.loading}
                        disabled={!this.isGenerateButtonEnabled() || this.props.loading}
                        onClick={this.handleGenerateDiagramClicked}
                    />
                </FooterSection>
            </SidebarBody>
        );
    }
}

const mapStateToProps = () =>
    createStructuredSelector({
        reagentInfo: selectDatasetReagentInfo(),
        isFetchingReagentInfo: isFetchingDatasetReagentInfo(),
    });

const mapDispatchToProps = (dispatch: ReduxDispatch) =>
    bindActionCreators(
        {
            fetchReagentInfoOfDataset,
        },
        dispatch
    );


export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(ThreeDSensitivitySidebarSection));
